import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ContractListItem,
  CounterpartiesTotals,
  Counterparty,
  CounterpartyContractPnl,
  CounterpartyListItem,
  CounterpartyM2m,
  CounterpartyPnl,
  CounterpartyPsgCosts,
  CounterpartyTableItem,
} from './types';

export const counterpartiesApi = createApi({
  reducerPath: 'counterpartiesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/counterparties',
  }),
  tagTypes: ['Counterparties'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getCounterpartiesI: builder.infiniteQuery<
      (CounterpartyListItem & { id: string })[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '',
        params: { ...queryArg, ...pageParam },
      }),
      transformResponse: (response: CounterpartyListItem[]) =>
        response.map(item => ({ ...item, id: item.counterpartyId })),
      providesTags: () => ['Counterparties'],
    }),
    getCounterpartiesWithSearchI: builder.infiniteQuery<
      (CounterpartyListItem & { id: string })[],
      (FiltersGrid & { search: string }) | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/search',
        params: { ...queryArg, ...pageParam },
      }),
      transformResponse: (response: CounterpartyListItem[]) =>
        response.map(item => ({ ...item, id: item.counterpartyId })),
      providesTags: () => ['Counterparties'],
    }),
    // Counterparty table page only; pickers and filters use getCounterpartiesI.
    getCounterpartiesTable: builder.infiniteQuery<
      (CounterpartyTableItem & { id: string })[],
      (FiltersGrid & { search: string }) | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      transformResponse: (response: CounterpartyTableItem[]) =>
        response.map(item => ({ ...item, id: item.counterpartyId })),
      providesTags: () => ['Counterparties'],
    }),
    getCounterpartiesTableTotals: builder.query<
      CounterpartiesTotals,
      { filter?: string; search?: string }
    >({
      query: params => ({ url: '/query/totals', params }),
      providesTags: () => ['Counterparties'],
    }),
    getCounterpartiesTotals: builder.query<
      CounterpartiesTotals,
      { filter?: string; search?: string }
    >({
      query: params => ({ url: '/search/totals', params }),
      providesTags: () => ['Counterparties'],
    }),
    getCounterpartyById: builder.query<Counterparty, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_, __, id) => [{ type: 'Counterparties', id }],
    }),
    getCounterpartyContracts: builder.query<
      ContractListItem[],
      { counterpartyId: string }
    >({
      query: ({ counterpartyId }) => ({ url: `/${counterpartyId}/contracts` }),
      providesTags: (_, __, { counterpartyId }) => [
        { type: 'Counterparties', id: counterpartyId },
      ],
    }),
    getCounterpartyPnl: builder.query<
      CounterpartyPnl,
      { counterpartyId: string; from?: string; to?: string }
    >({
      query: ({ counterpartyId, from, to }) => ({
        url: `/${counterpartyId}/pnl`,
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
      }),
      providesTags: (_, __, { counterpartyId }) => [
        { type: 'Counterparties', id: counterpartyId },
      ],
    }),
    // Day bounds ("yyyy-MM-dd"), not months: the contract P&L is kept per gas day (PO, 2026-09-14).
    // Without `contractId` the contracts come with their totals only; naming one returns it alone,
    // with its days — the card's list and its contract dialog (PO, 2026-09-26).
    getCounterpartyPnlDays: builder.query<
      CounterpartyContractPnl,
      {
        counterpartyId: string;
        from?: string;
        to?: string;
        contractId?: string;
      }
    >({
      query: ({ counterpartyId, from, to, contractId }) => ({
        url: `/${counterpartyId}/pnl/days`,
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(contractId ? { contractId } : {}),
        },
      }),
      providesTags: (_, __, { counterpartyId }) => [
        { type: 'Counterparties', id: counterpartyId },
      ],
    }),
    // No period: each contract is marked over its own remaining term, from the current gas day to
    // its end date (PO, 2026-09-23), so the card's month picker does not apply to it.
    getCounterpartyM2m: builder.query<
      CounterpartyM2m,
      { counterpartyId: string }
    >({
      query: ({ counterpartyId }) => ({
        url: `/${counterpartyId}/m2m`,
      }),
      providesTags: (_, __, { counterpartyId }) => [
        { type: 'Counterparties', id: counterpartyId },
      ],
    }),
    // Not period-scoped on purpose: a lot's ПСГ cost snapshot is a running to-date figure for the
    // whole lot, not a monthly series, so the P&L card's month range does not apply to it.
    getCounterpartyPsgCosts: builder.query<
      CounterpartyPsgCosts,
      { counterpartyId: string }
    >({
      query: ({ counterpartyId }) => ({
        url: `/${counterpartyId}/psg-costs`,
      }),
      providesTags: (_, __, { counterpartyId }) => [
        { type: 'Counterparties', id: counterpartyId },
      ],
    }),
  }),
});

export const useGetCounterpartiesInfiniteQuery = normalizeInfiniteQueryData(
  counterpartiesApi.useGetCounterpartiesIInfiniteQuery,
);

export const useGetCounterpartiesWithSearchIInfiniteQuery =
  normalizeInfiniteQueryData(
    counterpartiesApi.useGetCounterpartiesWithSearchIInfiniteQuery,
  );

export const useGetCounterpartiesTableInfiniteQuery =
  normalizeInfiniteQueryData(
    counterpartiesApi.useGetCounterpartiesTableInfiniteQuery,
  );

export const {
  useGetCounterpartiesTotalsQuery,
  useGetCounterpartiesTableTotalsQuery,
  useGetCounterpartyByIdQuery,
  useGetCounterpartyContractsQuery,
  useGetCounterpartyPnlQuery,
  useGetCounterpartyPnlDaysQuery,
  useGetCounterpartyM2mQuery,
  useGetCounterpartyPsgCostsQuery,
} = counterpartiesApi;
